import { useCallback } from 'react';
import {
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetEventDetailsQuery
} from '../store/services/api';
import type { CreateEventRequest, Event } from '../types/event';

export const useEventOperations = (eventId?: number, eventUrl?: string) => {
  // RTK Query hooks
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const { 
    data: eventDetails,
    isLoading: isLoadingDetails,
    error: detailsError
  } = useGetEventDetailsQuery(eventId || '', { 
    skip: !eventId 
  });

  // Create new event
  const handleCreateEvent = useCallback(async (eventData: CreateEventRequest) => {
    try {
      const result = await createEvent(eventData).unwrap();
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create event');
    }
  }, [createEvent]);

  // Update existing event
  const handleUpdateEvent = useCallback(async (eventData: CreateEventRequest) => {
    if (!eventUrl) {
      throw new Error('Event URL is required for update');
    }

    try {
      const result = await updateEvent({
        url: eventUrl,
        data: eventData
      }).unwrap();
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update event');
    }
  }, [eventUrl, updateEvent]);

  // Delete event
  const handleDeleteEvent = useCallback(async () => {
    if (!eventUrl) {
      throw new Error('Event URL is required for deletion');
    }

    try {
      const result = await deleteEvent(eventUrl).unwrap();
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete event');
    }
  }, [eventUrl, deleteEvent]);

  return {
    // Event operations
    createEvent: handleCreateEvent,
    updateEvent: handleUpdateEvent,
    deleteEvent: handleDeleteEvent,
    
    // Event details
    eventDetails,
    
    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isLoadingDetails,
    
    // Errors
    detailsError
  };
}; 